/**
 * AppShell — chrome de l'application Nestor (Editorial Operations).
 *
 * Refactor HiFi.4 : adopte le langage visuel « Editorial Operations »
 *
 *   - Mobile : HeaderBar minimaliste (gauche menu, droite search+bell
 *     — pas de wordmark central pour respecter l'encoche iOS). Le
 *     `mode-badge` du foyer courant reste cliquable depuis la
 *     HeaderBar pour préserver le flux de bascule de foyer mobile.
 *   - Desktop ≥ 900 px : Sidebar 240 px sur fond `--panel-2`, filet
 *     vertical `--rule`. Structure verticale :
 *       1. `NestorMark` wordmark
 *       2. Carte foyer cliquable (même target que `mode-badge` mobile)
 *       3. Nav verticale (icônes Lucide stroke 1.5)
 *       4. Footer : nom utilisateur + bouton settings
 *   - BottomNav mobile : frostée (`backdrop-filter`), 6/5 onglets
 *     icônes Lucide + label + soulignement actif 14×2 px en
 *     `--accent-deep`.
 *
 * Garde-fous CLAUDE.md §2.1 préservés :
 *   - 6 onglets en mode classique (Accueil, Planning, Repas, Photos,
 *     Messages, Foyer) — l'ordre suit l'Editorial Operations design,
 *     les routes restent identiques pour la rétrocompat URL.
 *   - 5 onglets en mode co-parentage (cp_home, cp_activities,
 *     cp_liaison, cp_frais, cp_settings).
 *   - Couleurs : tokens Editorial via `data-mode` (sage/slate).
 *
 * data-testid contractuels (E2E) préservés :
 *   - `mode-badge` (clickable, ouvre home picker)
 *   - `app-header`, `page-content`
 *   - `nav-sidebar-<route>`, `nav-bottom-<route>`
 *   - `home-picker-overlay`, `home-picker-empty`, `home-picker-<id>`
 */
import { html, render, type TemplateResult } from 'lit-html';
import { keyed } from 'lit-html/directives/keyed.js';
import styles from './AppShell.module.css';
import { subscribeLang, t, type TKey } from '../i18n/index.js';
import {
  currentRoute,
  initRouter,
  modeOf,
  navigate,
  type AppMode,
  type Route,
} from '../lib/router.js';
import {
  getCurrentHome,
  getCurrentHomeId,
  getHomes,
  subscribeHomes,
  switchHome,
} from '../stores/homes.js';
import type { HomeId } from '@nestor/shared-types';
import { subscribePreferences } from '../stores/preferences.js';
import { closeModal, getModal, openModal, subscribeUi } from '../stores/ui.js';
import { subscribeSession } from '../stores/session.js';
import { getMutationsQueueState, subscribeMutationsQueue } from '../stores/mutationsQueueStore.js';
import { syncThemeColor } from './themeColor.js';
import { lightImpact } from '../lib/capacitor/haptics.js';
import { Icon, Icons, type IconShape } from '../ui/icons.js';
import { nestorMark } from '../ui/NestorMark.js';
import { renderHomeView } from '../modules/home/View.js';
import { subscribeWeather } from '../modules/home/weather.js';
import { renderPlanningView } from '../modules/planning/View.js';
import { renderMessagesView } from '../modules/messages/View.js';
import { renderPhotosView } from '../modules/photos/View.js';
import { renderMealsView } from '../modules/meals/View.js';
import { renderSettingsView } from '../modules/settings/View.js';
import { renderCpHomeView } from '../modules/coparenting/HomeView.js';
import { renderCpActivitiesView } from '../modules/coparenting/ActivitiesView.js';
import { renderCpLiaisonView } from '../modules/coparenting/LiaisonView.js';
import { renderCpFraisView } from '../modules/coparenting/FraisView.js';
import { renderCpBagView } from '../modules/coparenting/BagView.js';
import { renderCpSchoolView } from '../modules/coparenting/SchoolView.js';
import { renderCpSettingsView } from '../modules/coparenting/SettingsView.js';
import { renderOnboardingView } from '../modules/onboarding/View.js';
import { renderInviteView } from '../modules/invite/View.js';
import { renderLoginView } from '../modules/auth/LoginView.js';

interface NavItem {
  route: Route;
  label: TKey;
  icon: IconShape;
}

/** Clé `ui.modal` du popover de bascule de foyer (mode-badge click). */
const MODAL_HOME_PICKER = 'app:home-picker';

/**
 * Mode classique — 6 onglets, ordre Editorial Operations :
 * Accueil → Planning → Repas → Photos → Messages → Réglages.
 *
 * Le 6ᵉ onglet pointe sur `/settings`, libellé « Réglages » + icône
 * engrenage (`Icons.settings`). L'ancien engrenage redondant du footer
 * sidebar a été retiré (alpha) — l'accès aux réglages passe par cet
 * onglet (et par `/cp/settings` en mode co-parentage).
 */
const CLASSIC_NAV: readonly NavItem[] = [
  { route: '/', label: 'home', icon: Icons.home },
  { route: '/planning', label: 'planning_merged', icon: Icons.calendar },
  { route: '/meals', label: 'meals', icon: Icons.chef },
  { route: '/photos', label: 'photos', icon: Icons.image },
  { route: '/messages', label: 'messages', icon: Icons.message },
  { route: '/settings', label: 'settings', icon: Icons.settings },
];

const COPARENTING_NAV: readonly NavItem[] = [
  { route: '/cp/', label: 'cp_home', icon: Icons.home },
  { route: '/cp/activities', label: 'cp_activities', icon: Icons.calendar },
  { route: '/cp/liaison', label: 'cp_liaison', icon: Icons.book },
  { route: '/cp/frais', label: 'cp_frais', icon: Icons.euro },
  // Alpha : Scolarité accessible directement (avant : via Réglages CP).
  { route: '/cp/school', label: 'cp_school', icon: Icons.school },
  { route: '/cp/settings', label: 'cp_settings', icon: Icons.settings },
];

function navFor(mode: AppMode): readonly NavItem[] {
  return mode === 'coparenting' ? COPARENTING_NAV : CLASSIC_NAV;
}

function renderRouteContent(route: Route): TemplateResult {
  switch (route) {
    case '/':
      return renderHomeView();
    case '/messages':
      return renderMessagesView();
    case '/planning':
      return renderPlanningView();
    case '/photos':
      return renderPhotosView();
    case '/meals':
      return renderMealsView();
    case '/settings':
      return renderSettingsView();
    case '/cp/':
      return renderCpHomeView();
    case '/cp/activities':
      return renderCpActivitiesView();
    case '/cp/liaison':
      return renderCpLiaisonView();
    case '/cp/frais':
      return renderCpFraisView();
    case '/cp/settings':
      return renderCpSettingsView();
    case '/cp/bag':
      return renderCpBagView();
    case '/cp/school':
      return renderCpSchoolView();
    case '/onboarding':
      return renderOnboardingView();
    case '/invite':
      return renderInviteView();
    case '/login':
      return renderLoginView();
  }
}

/**
 * Item de nav — partage le markup entre sidebar et bottom-nav. Le
 * `kind` détermine le layout (`sidebar` = horizontal icon+label,
 * `bottom` = vertical icon-au-dessus-label avec soulignement actif).
 */
function navItemTemplate(
  item: NavItem,
  current: Route,
  kind: 'sidebar' | 'bottom',
): TemplateResult {
  const active = item.route === current;
  const onClick = (e: Event): void => {
    e.preventDefault();
    // Phase 7.3 : retour haptique léger au changement d'onglet (no-op
    // web — n'a d'effet qu'en natif via Capacitor Haptics).
    void lightImpact();
    navigate(item.route);
  };
  const iconStroke = active ? 1.8 : 1.5;
  if (kind === 'bottom') {
    return html`<a
      href="#${item.route}"
      aria-current=${active ? 'page' : 'false'}
      data-testid="nav-bottom-${item.route}"
      @click=${onClick}
    >
      ${Icon(item.icon, 20, { stroke: iconStroke })}
      <span class=${styles.bottomNavLabel}>${t(item.label)}</span>
      <span class=${styles.bottomNavUnderline}></span>
    </a>`;
  }
  return html`<a
    href="#${item.route}"
    aria-current=${active ? 'page' : 'false'}
    data-testid="nav-sidebar-${item.route}"
    @click=${onClick}
  >
    ${Icon(item.icon, 18, { stroke: iconStroke })}
    <span>${t(item.label)}</span>
  </a>`;
}

/**
 * Bouton mode-badge (mobile, HeaderBar) — ouvre / ferme le home picker.
 * Visuel Editorial : dot + kicker uppercase. Garde le `data-testid
 * ="mode-badge"` contractuel pour les E2E qui exercent la bascule de
 * foyer.
 */
function modeBadgeButton(label: string, mode: AppMode, homeId: HomeId | null): TemplateResult {
  const isOpen = getModal() === MODAL_HOME_PICKER;
  const dotColor = mode === 'coparenting' ? 'var(--slate)' : 'var(--sage)';
  const onClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen) closeModal();
    else openModal(MODAL_HOME_PICKER);
  };
  return html`<button
    type="button"
    class=${styles.headerBadgeBtn}
    data-testid="mode-badge"
    data-home-id=${homeId ?? ''}
    aria-haspopup="listbox"
    aria-expanded=${isOpen ? 'true' : 'false'}
    @click=${onClick}
  >
    <span class=${styles.headerBadgeDot} style="background: ${dotColor}"></span>
    <span class="kicker">${label}</span>
  </button>`;
}

/**
 * Badge « X mutations en attente » — Phase 3.3.4.
 *
 * Affiché dans le slot droit du header quand `pendingCount > 0`.
 * Discret (border-radius pill, fond sourd) — pas alarmant, cohérent
 * avec le ton bienveillant (CLAUDE.md §2.1). Le label vient d'i18n
 * (`mutations_pending_badge`), le nombre est rendu en `tnum` pour
 * éviter les sauts de largeur.
 *
 * Pas de bouton — purement informationnel en 3.3.4. Cliquer un jour
 * pourrait ouvrir une vue détaillée de la queue (3.3.5+).
 */
function pendingMutationsBadge(): TemplateResult {
  const { pendingCount } = getMutationsQueueState();
  if (pendingCount <= 0) return html``;
  return html`<span
    class=${styles.pendingBadge}
    data-testid="mutations-pending-badge"
    role="status"
    aria-label=${`${pendingCount.toString()} ${t('mutations_pending_badge')}`}
  >
    <span class=${styles.pendingBadgeDot} aria-hidden="true"></span>
    <span class=${styles.pendingBadgeCount}>${pendingCount}</span>
    <span>${t('mutations_pending_badge')}</span>
  </span>`;
}

/**
 * Carte foyer cliquable de la sidebar desktop — même fonctionnalité que
 * le `mode-badge` mobile (ouvre le home picker), mais visuellement
 * différente : une carte 12 × 14 px avec dot + nom + kicker mode +
 * chevron à droite.
 */
function sidebarHomeCard(label: string, mode: AppMode, homeId: HomeId | null): TemplateResult {
  const isOpen = getModal() === MODAL_HOME_PICKER;
  const dotColor = mode === 'coparenting' ? 'var(--slate)' : 'var(--sage)';
  const onClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen) closeModal();
    else openModal(MODAL_HOME_PICKER);
  };
  const modeLabel: TKey = mode === 'coparenting' ? 'cp_home' : 'home';
  return html`<button
    type="button"
    class=${styles.sidebarHomeCard}
    data-testid="mode-badge"
    data-home-id=${homeId ?? ''}
    aria-haspopup="listbox"
    aria-expanded=${isOpen ? 'true' : 'false'}
    @click=${onClick}
  >
    <span class=${styles.headerBadgeDot} style="background: ${dotColor}"></span>
    <span>
      <div class=${styles.sidebarHomeCardName}>${label}</div>
      <div class=${styles.sidebarHomeCardKicker}>${t(modeLabel)}</div>
    </span>
    <span class=${styles.sidebarHomeCardChevron}>${Icon(Icons.chevronDown, 14)}</span>
  </button>`;
}

function homePickerTemplate(): TemplateResult {
  const homes = getHomes();
  const activeId = getCurrentHomeId();
  const others = homes.filter((h) => h.id !== activeId);
  const onPick = (id: HomeId): void => {
    if (id === activeId) {
      closeModal();
      return;
    }
    switchHome(id);
    closeModal();
    // `reconcileRouteWithHome` redirigera vers la home du nouveau mode
    // au prochain draw.
  };
  const onOverlay = (e: Event): void => {
    if (e.target === e.currentTarget) closeModal();
  };
  const stop = (e: Event): void => {
    e.stopPropagation();
  };
  return html`<div
    class=${styles.homePickerOverlay}
    data-testid="home-picker-overlay"
    @click=${onOverlay}
  >
    <div
      class=${styles.homePickerPanel}
      role="listbox"
      aria-label=${t('switch_home_title')}
      @click=${stop}
    >
      <div class=${styles.homePickerHeader}>${t('switch_home_title')}</div>
      ${others.length === 0
        ? html`<div class=${styles.homePickerEmpty} data-testid="home-picker-empty">
            ${t('switch_home_none_other')}
          </div>`
        : html`<ul class=${styles.homePickerList}>
            ${others.map(
              (h) =>
                html`<li>
                  <button
                    type="button"
                    class=${styles.homePickerItem}
                    data-testid="home-picker-${h.id}"
                    data-home-mode=${h.mode}
                    @click=${(): void => onPick(h.id)}
                  >
                    <span class=${styles.homePickerDot} data-mode=${h.mode}></span>
                    <span class=${styles.homePickerName}>${h.name}</span>
                    <span class=${styles.homePickerMode}
                      >${t(h.mode === 'coparenting' ? 'cp_home' : 'home')}</span
                    >
                  </button>
                </li>`,
            )}
          </ul>`}
    </div>
  </div>`;
}

function shellTemplate(route: Route, mode: AppMode): TemplateResult {
  // Routes plein écran (onboarding, login) : rendues sans la chrome
  // (sidebar / header / bottom-nav). Cf. CLAUDE.md §2.1 — l'onboarding
  // est volontairement immersif.
  if (route === '/onboarding' || route === '/login') {
    return html`<div class=${styles.shell} data-mode=${mode}>${renderRouteContent(route)}</div>`;
  }

  const items = navFor(mode);
  const titleKey: TKey = mode === 'coparenting' ? 'cp_home' : 'home';
  const home = getCurrentHome();
  const badgeText = home?.name ?? t(titleKey);
  const showPicker = getModal() === MODAL_HOME_PICKER;

  return html`
    <div class=${styles.shell} data-mode=${mode}>
      <aside class=${styles.sidebar} aria-label="Navigation principale">
        <div class=${styles.sidebarBrand}>${nestorMark({ size: 22 })}</div>
        ${sidebarHomeCard(badgeText, mode, home?.id ?? null)}
        <nav class=${styles.sidebarNav}>
          ${items.map((item) => navItemTemplate(item, route, 'sidebar'))}
        </nav>
        <div class=${styles.sidebarFooter} data-testid="sidebar-footer">
          <span class=${styles.sidebarFooterName}>ALPHA 1.0</span>
        </div>
      </aside>
      <main class=${styles.main}>
        <header class=${styles.header} data-testid="app-header">
          <!-- Feedback tel : plus d'engrenage ici — Réglages est un
               onglet de la barre. Slot conservé pour centrer le badge. -->
          <div class=${styles.headerSlot}></div>
          ${modeBadgeButton(badgeText, mode, home?.id ?? null)}
          <div class=${styles.headerSlot} style="justify-content: flex-end;">
            ${pendingMutationsBadge()}
          </div>
        </header>
        <section class=${styles.content} data-testid="page-content">
          ${keyed(route, html`<div class=${styles.routeFade}>${renderRouteContent(route)}</div>`)}
        </section>
        <nav class=${styles.bottomNav} aria-label="Navigation principale">
          ${items.map((item) => navItemTemplate(item, route, 'bottom'))}
        </nav>
      </main>
      ${showPicker ? homePickerTemplate() : html``}
    </div>
  `;
}

/**
 * Garantit la cohérence URL ↔ mode du foyer courant (ADR 0004 règle 2).
 *
 * Si l'utilisateur arrive sur une route co-parentage alors que le foyer
 * courant est en mode classique (ou inversement), on réécrit l'URL via
 * `replaceState` (pas d'item dans l'historique) vers la home du mode du
 * foyer courant.
 *
 * Tant qu'aucun foyer n'existe (cas démarrage à froid), on ne redirige
 * pas — l'onboarding (incrément 19) prend le relais via `guardOnboarding`.
 *
 * Retourne `true` si une redirection a eu lieu (auquel cas le draw du
 * caller peut être annulé : un nouveau cycle est en route).
 */
function reconcileRouteWithHome(route: Route): boolean {
  const home = getCurrentHome();
  if (!home) return false;
  if (route === '/onboarding' || route === '/login') return false;

  const routeMode = modeOf(route);
  if (routeMode === home.mode) return false;

  navigate(home.mode === 'coparenting' ? '/cp/' : '/', { replace: true });
  return true;
}

/**
 * Guard onboarding (Phase 1, incrément 19). Si aucun foyer n'existe
 * et que la route demandée n'est pas `/onboarding` ou `/login`, on
 * redirige vers `/onboarding` pour démarrer le flow 2 étapes.
 */
function guardOnboarding(route: Route): boolean {
  if (getHomes().length > 0) return false;
  // Phase 6.2 : `/invite` est aussi un point d'entrée légitime
  // pour un user qui n'a pas encore de foyer (il rejoint via
  // invitation). On laisse passer.
  if (route === '/onboarding' || route === '/login' || route === '/invite') return false;
  navigate('/onboarding', { replace: true });
  return true;
}

/**
 * Monte l'AppShell sur l'élément racine et s'abonne aux sources de
 * vérité : router (route active), i18n (langue), homes (foyer courant),
 * preferences, ui.
 */
export function mountAppShell(root: HTMLElement): () => void {
  let lastMode: AppMode | null = null;
  let lastHomeId: HomeId = getCurrentHomeId();

  const draw = (route: Route): void => {
    if (guardOnboarding(route)) return;
    if (reconcileRouteWithHome(route)) return;
    const mode = modeOf(route);
    document.body.dataset['mode'] = mode;
    if (mode !== lastMode) {
      syncThemeColor(mode);
      lastMode = mode;
    }
    render(shellTemplate(route, mode), root);
  };

  const unsubscribeRouter = initRouter(draw);
  const unsubscribeLang = subscribeLang(() => {
    draw(currentRoute());
  });
  const unsubscribeHomes = subscribeHomes(() => {
    const newId = getCurrentHomeId();
    if (newId !== lastHomeId) {
      lastHomeId = newId;
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
          window.scrollTo(0, 0);
        }
      }
    }
    draw(currentRoute());
  });
  const unsubscribePrefs = subscribePreferences(() => {
    draw(currentRoute());
  });
  const unsubscribeUi = subscribeUi(() => {
    draw(currentRoute());
  });
  // Phase 4.3 : re-render quand le statut session change (login,
  // logout, init terminée). La section « Mon compte » dans Settings
  // dépend de `getSession()` — sans cet abonnement, elle reste figée
  // sur l'état initial `loading` jusqu'à un autre trigger.
  const unsubscribeSession = subscribeSession(() => {
    draw(currentRoute());
  });
  // Phase 3.3.4 : re-render quand le compteur de mutations en attente
  // change. Permet d'afficher / masquer le badge "X en attente" sur
  // le header sans poller. L'abonnement reste actif sans coût quand
  // le compte est à 0 (le rendu conditionne via `pendingCount > 0`).
  const unsubscribeMutationsQueue = subscribeMutationsQueue(() => {
    draw(currentRoute());
  });
  // Météo (accueil) : re-render quand l'état météo change — géoloc
  // obtenue, fetch Open-Meteo résolu, ou rafraîchissement de fond.
  const unsubscribeWeather = subscribeWeather(() => {
    draw(currentRoute());
  });

  return () => {
    unsubscribeRouter();
    unsubscribeLang();
    unsubscribeHomes();
    unsubscribePrefs();
    unsubscribeUi();
    unsubscribeSession();
    unsubscribeMutationsQueue();
    unsubscribeWeather();
  };
}
